import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useVoiceMessages,
  voiceMessages$,
} from "@jugl-web/rest-api/integrations/hooks/useVoiceMessages";
import { VoiceMessageModel } from "@jugl-web/rest-api/integrations/models/VoiceMessage";
import { LoadingAnimation, DataLoadingWrapper } from "@jugl-web/ui-kit";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { VList, VListHandle } from "virtua";
import { isoToLocalDate, useDebounce, useTranslations } from "@jugl-web/utils";
import { format, isThisYear, isToday } from "date-fns";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { ChatDateHeader } from "@web-src/modules/chats/pages/ChatsPage/components/ChatWindow/components/ChatMessagesWindow/components/ChatDateHeader";
import { ReactComponent as ChevronsDownIcon } from "../WhatsappChatMessageWindow/assets/chevrons-down.svg";
import { VoiceMessageItem } from "../VoiceMessageItem";

type VoiceChatMessageItem =
  | { type: "message"; value: VoiceMessageModel }
  | { type: "date"; value: string };

export const VoiceChatMessageWindow: FC<{
  conversationId: string;
}> = ({ conversationId }) => {
  const { entityId } = useEntitySelectedProvider();
  const vListRef = useRef<VListHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const debouncedAtBottom = useDebounce(atBottom, 500);
  const { dateLocale } = useLanguage();
  const { t } = useTranslations();
  const prevLenRef = useRef(0);
  const prevScrollOffsetRef = useRef(0);
  const wasLoadingMore = useRef(false);

  const [isInitialScrollInitialized, setIsInitialScrollInitialized] =
    useState(false);
  const isInitialScrollInitializationInProgress = useRef(false);

  const {
    data: loadedMessages,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reset,
  } = useVoiceMessages({
    entityId,
    conversationId,
    pageSize: 50,
  });

  const voiceMessageItems = useMemo(() => {
    if (!loadedMessages.length) return [];

    const result: VoiceChatMessageItem[] = [];
    const addedDates: string[] = [];

    loadedMessages.forEach((message) => {
      const date = isoToLocalDate(message.payload.timestamp);
      const dateStrAlt = format(date, "yyyy-MM-dd");

      if (!addedDates.includes(dateStrAlt)) {
        addedDates.push(dateStrAlt);
        const dateStr = isToday(date)
          ? t({
              id: "common.today",
              defaultMessage: "Today",
            })
          : format(date, isThisYear(date) ? "d MMMM" : "d MMMM yyyy", {
              locale: dateLocale,
            });

        result.push({ type: "date", value: dateStr });
      }

      result.push({ type: "message", value: message });
    });

    return result;
  }, [loadedMessages, t, dateLocale]);

  useEffect(() => {
    setIsInitialScrollInitialized(false);
    reset();
  }, [conversationId, reset]);

  const scrollToBottom = useCallback(() => {
    vListRef.current?.scrollToIndex(voiceMessageItems.length, {
      align: "end",
    });
    setAtBottom(true);
  }, [voiceMessageItems.length]);

  useEffect(() => {
    const subscription = voiceMessages$.subscribe((action) => {
      if (action.type !== "append") return;
      if (action.message.conv_id !== conversationId) return;

      if (atBottom) {
        setTimeout(scrollToBottom, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, [conversationId, atBottom, scrollToBottom]);

  useEffect(() => {
    if (
      !isInitialScrollInitialized &&
      !isInitialScrollInitializationInProgress.current &&
      voiceMessageItems.length > 0 &&
      !isLoading
    ) {
      isInitialScrollInitializationInProgress.current = true;
      setTimeout(() => {
        vListRef.current?.scrollToIndex(voiceMessageItems.length - 1, {
          align: "end",
        });
        setIsInitialScrollInitialized(true);
        isInitialScrollInitializationInProgress.current = false;
      }, 100);
    }
  }, [
    voiceMessageItems.length,
    isLoading,
    isInitialScrollInitialized,
    scrollToBottom,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore) {
      prevLenRef.current = voiceMessageItems.length;
      prevScrollOffsetRef.current = vListRef.current?.scrollOffset ?? 0;
      wasLoadingMore.current = true;
      await loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, voiceMessageItems.length]);

  useEffect(() => {
    if (isLoadingMore) {
      wasLoadingMore.current = true;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (
      wasLoadingMore.current &&
      !isLoadingMore &&
      voiceMessageItems.length > prevLenRef.current
    ) {
      vListRef.current?.scrollToIndex(
        voiceMessageItems.length - prevLenRef.current,
        {
          align: "start",
          offset: prevScrollOffsetRef.current,
        }
      );
      wasLoadingMore.current = false;
    }
    prevLenRef.current = voiceMessageItems.length;
  }, [voiceMessageItems, isLoadingMore]);

  return (
    <div className="relative flex h-full flex-col bg-gray-100">
      <DataLoadingWrapper
        isError={isError}
        isFetching={false}
        isLoading={isLoading && loadedMessages.length === 0}
        onRetry={reset}
      >
        <VList
          ref={vListRef}
          reverse
          className="flex-1 pb-6"
          onScroll={(offset) => {
            if (
              !vListRef.current ||
              isLoadingMore ||
              !isInitialScrollInitialized
            ) {
              return;
            }
            const bottomOffset =
              offset -
              vListRef.current.scrollSize +
              vListRef.current.viewportSize;

            setAtBottom(bottomOffset > -5);
            if (offset < 150 && hasMore) {
              handleLoadMore();
            }
          }}
        >
          {isLoading && loadedMessages.length > 0 && hasMore && (
            <div className="flex justify-center py-4">
              <LoadingAnimation size="sm" />
            </div>
          )}
          {voiceMessageItems.map((item, index) => (
            <div
              key={item.type === "date" ? `date-${+index}` : item.value.id}
              className="mx-auto w-full max-w-[1000px]"
            >
              {item.type === "date" ? (
                <ChatDateHeader date={item.value} />
              ) : (
                <VoiceMessageItem
                  message={item.value}
                  messages={loadedMessages}
                />
              )}
            </div>
          ))}
        </VList>
      </DataLoadingWrapper>

      {!debouncedAtBottom && (
        <button
          type="button"
          onClick={() => scrollToBottom()}
          className="absolute right-4 bottom-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-primary-700 shadow-lg transition-opacity hover:opacity-90"
        >
          <ChevronsDownIcon className="text-white" />
        </button>
      )}
    </div>
  );
};
